import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, FormControlLabel, Paper, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { dashboardSettings, uploadContainerSettings } from "../utils/dashboardSettingsConstants";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { MenuContext } from "../App";
import useUploadFile, { UploadError } from "../hooks/useUploadFile";
import { ErrorDialog } from "../components/ErrorDialog";
import { AnnouncementDialog } from "../components/AnnouncementDialog";
import { INPUT_TEMPLATE_URL } from "../utils/links";

const ErrorContext = createContext<UploadError | null>(null);

// One outcome-labelled choice for how the row-level detail is delivered (maps to the backend's
// size-based spill policy). Summary sheets are ALWAYS kept in Excel regardless of the choice.
const DETAIL_OPTIONS: { value: string; label: string }[] = [
    { value: "auto", label: "Auto — one Excel file when it fits; very large detail spills to a zipped CSV (recommended)" },
    { value: "always_csv", label: "Always include CSV — Excel when it fits, plus the row-level detail as CSV every time" },
    { value: "csv_only", label: "CSV only — row-level detail as CSV; the Excel workbook keeps just the summary sheets" },
    { value: "summary_only", label: "Summary only — skip the row-level detail; just the summary workbook" },
];

export const Upload = () => {
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);

    const { setSelectedMenu } = useContext(MenuContext);

    const { upload, loading, error, reset, cancel } = useUploadFile();
    // How row-level detail is delivered (Excel inline / CSV / omitted). Default "auto": inline in
    // Excel while the file stays small, else spill the biggest sheets to a zipped CSV. Summary
    // sheets are always kept in Excel. See DETAIL_OPTIONS and the backend spill_policy.
    const [detailMode, setDetailMode] = useState("auto");
    // Selection is decoupled from running: the user picks/drops a file (shown by name), reviews the options,
    // then presses Run. So the tick-boxes are seen BEFORE the run action, and a wrong file isn't run on click.
    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    // Autorun (opt-in, remembered across tabs and sessions via localStorage): when on, picking/dropping a
    // file runs it immediately — no Run click. Safe to leave on: a mis-picked file can be stopped with Cancel.
    const [autorun, setAutorun] = useState<boolean>(() => localStorage.getItem("jim.autorun") === "1");

    useEffect(() => {
        setSelectedMenu("upload");
    }, [setSelectedMenu]);

    useEffect(() => {
        localStorage.setItem("jim.autorun", autorun ? "1" : "0");
    }, [autorun]);

    const handleClose = (event: {}, reason: string) => {
        if (reason && reason === "backdropClick") return;
    };

    const runFile = (f: File): void => {
        const formData = new FormData();
        formData.append("file", f);
        formData.append("detail_mode", detailMode);
        upload(formData);
    };

    const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const selected = e.target.files?.item(0);
        if (selected) {
            setFile(selected);
            if (autorun) runFile(selected);
        }
    };

    const onDrop = (e: React.DragEvent): void => {
        e.preventDefault();
        setDragOver(false);
        const dropped = e.dataTransfer.files?.item(0);
        if (dropped) {
            setFile(dropped);
            if (autorun) runFile(dropped);
        }
    };

    const openBrowse = (): void => {
        if (fileRef.current) {
            fileRef.current.value = ""; // clear so re-selecting the SAME file still fires onChange
            fileRef.current.click();
        }
    };

    return (
        <ErrorContext.Provider value={error}>
            <AnnouncementDialog />
            <Stack
                direction={{ xs: "column", md: "row" }}
                width={dashboardSettings.containerWidth}
                m="auto"
                mt={5}
                spacing={2}
                alignItems="flex-start"
            >
                <Dialog open={loading} onClose={(event, reason) => handleClose(event, reason)}>
                    <DialogContent sx={{ margin: "auto", textAlign: "center", px: 5, py: 4 }}>
                        <CircularProgress />
                        <Typography fontSize="14px" color="text.secondary" mt={2}>
                            Running the model — this can take a few minutes…
                        </Typography>
                        <Button variant="outlined" color="inherit" size="small" onClick={cancel} sx={{ mt: 3 }}>
                            Cancel run
                        </Button>
                    </DialogContent>
                </Dialog>

                {/* Main: upload + run */}
                <Paper sx={{ ...uploadContainerSettings.style, width: "auto", mt: 0, flexGrow: 1, minWidth: 0 }}>
                    <Stack p={3} spacing={2}>
                        <Typography fontSize="32px" fontWeight={500}>
                            {t("upload.input")}
                        </Typography>
                        <Typography fontSize="14px" color="text.secondary">
                            {t("upload.description")}
                        </Typography>

                        <input onChange={onFilePicked} type="file" accept=".xlsx" ref={fileRef} style={{ display: "none" }} />

                        {/* Drag-and-drop / click-to-browse zone — shows the selected file name */}
                        <Box
                            onClick={openBrowse}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            sx={{
                                border: "2px dashed",
                                borderColor: dragOver ? "primary.main" : "grey.400",
                                borderRadius: 1,
                                backgroundColor: dragOver ? "action.hover" : "transparent",
                                py: 4,
                                px: 2,
                                textAlign: "center",
                                cursor: "pointer",
                                transition: "border-color .15s, background-color .15s",
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 40, color: file ? "primary.main" : "grey.500" }} />
                            <Typography fontSize="14px" mt={1} sx={{ wordBreak: "break-all" }}>
                                {file ? file.name : "Drag your input file here, or click to browse"}
                            </Typography>
                            {file && (
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<CloseIcon fontSize="small" />}
                                    onClick={(e) => {
                                        e.stopPropagation(); // don't also trigger the box's browse-on-click
                                        setFile(null);
                                        if (fileRef.current) fileRef.current.value = "";
                                    }}
                                    sx={{ mt: 1, color: "text.secondary" }}
                                >
                                    Clear
                                </Button>
                            )}
                        </Box>

                        {/* Options ABOVE the run button, so they are seen/set before running */}
                        <Stack>
                            <Typography fontSize="14px" fontWeight={600} sx={{ mt: 1 }}>
                                Results detail
                            </Typography>
                            <RadioGroup
                                value={detailMode}
                                onChange={(e) => setDetailMode(e.target.value)}
                                sx={{ mt: 0.25, pl: 0.5 }}
                            >
                                {DETAIL_OPTIONS.map((o) => (
                                    <FormControlLabel
                                        key={o.value}
                                        value={o.value}
                                        control={<Radio size="small" sx={{ py: 0.25 }} />}
                                        label={<Typography fontSize="14px">{o.label}</Typography>}
                                        sx={{ my: -0.4, alignItems: "flex-start" }}
                                    />
                                ))}
                            </RadioGroup>
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                startIcon={<PlayArrowIcon />}
                                disabled={!file}
                                onClick={() => file && runFile(file)}
                            >
                                {t("upload.browseAndRun")}
                            </Button>
                            <FormControlLabel
                                control={<Checkbox size="small" checked={autorun} onChange={(e) => setAutorun(e.target.checked)} />}
                                label={<Typography fontSize="14px">Autorun — run as soon as a file is selected</Typography>}
                            />
                        </Stack>
                    </Stack>
                </Paper>

                {/* Aside: input template */}
                <Paper sx={{ width: { xs: "100%", md: "300px" }, flexShrink: 0, backgroundColor: "#FFFFFF", borderRadius: dashboardSettings.borderRadius, p: 3 }}>
                    <Stack spacing={1.5}>
                        <Typography fontSize="16px" fontWeight={600}>
                            {t("downloadFile.title")}
                        </Typography>
                        <Typography fontSize="14px" color="text.secondary">
                            {t("downloadFile.description")}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            href={INPUT_TEMPLATE_URL}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            {t("downloadFile.button")}
                        </Button>
                    </Stack>
                </Paper>
            </Stack>
            <ErrorDialog error={error} reset={reset} />
        </ErrorContext.Provider>
    );
};
